import {
  Tooltip,
  Tag,
  TagLabel,
  Flex,
  Spinner,
  Img,
  Box,
  IconButton,
  TagCloseButton,
} from '@chakra-ui/react';
import { FileUpload } from '@texas/types';
import { Icons } from '../Icons';
import { clientEndpoints } from '@texas/clientEndpoints';
import { fadeInAnimation } from '@texas/resources/animations/animations';

const imgSize = '160px';

export function FileUploads({
  fileUploads,
  removeFileUpload,
  onRemove,
}: {
  fileUploads: FileUpload[];
  removeFileUpload: (file: FileUpload) => void;
  onRemove?: (file: FileUpload) => void;
}) {
  return (
    <Flex gap={2} flexWrap="wrap">
      {fileUploads.map((upload) => {
        if (upload.type === 'failed')
          return (
            <Tooltip label={upload.error?.message} key={upload.uuid}>
              <Tag
                key={upload.uuid}
                variant="lighter"
                w="fit-content"
                py={2}
                height={8}
              >
                <TagLabel mr={2}>{upload.fileName}</TagLabel>
                <Icons.Close
                  boxSize={4}
                  color="red.300"
                  _light={{ color: 'red.500' }}
                />
              </Tag>
            </Tooltip>
          );
        return (
          <RenderFile
            upload={upload}
            abort={removeFileUpload}
            key={upload.uuid}
            onRemove={onRemove}
          />
        );
      })}
    </Flex>
  );
}

function RenderFile({
  upload,
  abort,
  onRemove,
}: {
  upload: FileUpload;
  abort: (file: FileUpload) => void;
  onRemove?: (file: FileUpload) => void;
}) {
  const src = renderImgSrc(upload);
  return (
    <Tooltip label={upload.fileName}>
      <Box
        key={upload.uuid}
        borderRadius="md"
        boxSize="fit-content"
        pos="relative"
        animation={fadeInAnimation()}
        role="group"
        boxShadow="lg"
      >
        {src ? (
          <Img maxH={imgSize} maxW={imgSize} src={src} />
        ) : (
          <RenderFileTag upload={upload} removeFileUpload={abort} />
        )}

        {upload.type === 'uploading' && (
          <Flex
            alignItems="center"
            pos="absolute"
            inset={0}
            zIndex={1}
            backgroundColor="#00000080"
            justifyContent="center"
            gap={2}
          >
            <Spinner size="md" />
            <IconButton
              variant="ghost"
              icon={<Icons.Close />}
              aria-label="Close"
              onClick={() => abort(upload)}
            />
          </Flex>
        )}
        {upload.type !== 'uploading' && onRemove && (
          <Flex
            alignItems="center"
            pos="absolute"
            inset={0}
            zIndex={1}
            backgroundColor="#00000090"
            justifyContent="center"
            gap={2}
            display="none"
            _groupHover={{ display: 'flex' }}
          >
            <IconButton
              variant="ghost"
              icon={<Icons.Close />}
              aria-label="Close"
              onClick={() => onRemove(upload)}
            />
          </Flex>
        )}
      </Box>
    </Tooltip>
  );
}

function RenderFileTag({
  upload,
  removeFileUpload,
}: {
  upload: FileUpload;
  removeFileUpload: (file: FileUpload) => void;
}) {
  return (
    <Tag
      key={upload.uuid}
      variant="lighter"
      h="fit-content"
      w="fit-content"
      py={2}
      height={8}
    >
      <TagLabel mr={2}>{upload.fileName}</TagLabel>
      {upload.type === 'uploading' && (
        <Flex alignItems="center">
          <Spinner size="xs" />
          <TagCloseButton onClick={() => removeFileUpload(upload)} />
        </Flex>
      )}
      {upload.type === 'uploaded' && (
        <Icons.Checkmark
          boxSize={4}
          color="green.300"
          _light={{ color: 'green.500' }}
        />
      )}
    </Tag>
  );
}

function renderImgSrc(file: FileUpload) {
  if (file.type === 'uploaded' && file.previewIdentifier) {
    return clientEndpoints.previewImage(file.previewIdentifier, 128);
  }

  return file.renderUrl;
}
